/**
 * Marketplace fee data — updated 2026-Q1
 *
 * Sources:
 * - Bol.com: partnerplatform.bol.com (Voordeeltrede 1)
 * - Amazon: sellercentral.amazon.nl (EU standard rates)
 * - Etsy: etsy.com/legal/fees
 * - Shopify: shopify.com/pricing
 */

// ─── Bol.com ────────────────────────────────────────────────────────

export interface BolShippingTier {
  id: string;
  dimensions: string;
  maxWeight: string;
  vvbCost: number;
  fixedContribution: number;
}

export interface BolCommissionCategory {
  id: string;
  group: string;
  label: string;
  rate: number;
}

export const BOL_DATA_UPDATED = '2026-04-19';

export const BOL_SHIPPING_TIERS: BolShippingTier[] = [
  { id: 'brievenbuspakket', dimensions: '38 × 26,5 × 3,2 cm', maxWeight: '2 kg', vvbCost: 4.15, fixedContribution: 1.96 },
  { id: 'standaard', dimensions: '100 × 50 × 50 cm', maxWeight: '23 kg', vvbCost: 6.13, fixedContribution: 2.95 },
  { id: 'groot', dimensions: '> 100 cm of oversize', maxWeight: '31,5 kg', vvbCost: 13.20, fixedContribution: 4.25 },
  { id: 'extra-groot', dimensions: '> 150 cm', maxWeight: '> 31,5 kg', vvbCost: 19.50, fixedContribution: 5.95 },
];

export const BOL_BE_SURCHARGE = 2.50;
export const BOL_Q4_SURCHARGE_FACTOR = 1.40;
export const BOL_VAT_RATE = 0.21;

export const BOL_COMMISSION_CATEGORIES: BolCommissionCategory[] = [
  // Elektronica & Computers
  { id: 'computers-tablets', group: 'Elektronica & Computers', label: 'Computers & tablets', rate: 0.07 },
  { id: 'smartphones', group: 'Elektronica & Computers', label: 'Smartphones & telefoons', rate: 0.07 },
  { id: 'tvs-audio', group: 'Elektronica & Computers', label: "TV's & audio", rate: 0.07 },
  { id: 'camera-accessoires', group: 'Elektronica & Computers', label: 'Camera-accessoires', rate: 0.08 },
  { id: 'kleine-elektronica', group: 'Elektronica & Computers', label: 'Kleine elektronica & accessoires', rate: 0.09 },
  { id: 'kabels-adapters', group: 'Elektronica & Computers', label: 'Kabels & adapters', rate: 0.12 },
  // Huis & Tuin
  { id: 'meubels', group: 'Huis & Tuin', label: 'Meubels', rate: 0.12 },
  { id: 'tuinartikelen', group: 'Huis & Tuin', label: 'Tuinartikelen', rate: 0.12 },
  { id: 'huishoudelijke-apparaten', group: 'Huis & Tuin', label: 'Huishoudelijke apparaten', rate: 0.09 },
  { id: 'keukenbenodigdheden', group: 'Huis & Tuin', label: 'Keukenbenodigdheden', rate: 0.12 },
  { id: 'verlichting', group: 'Huis & Tuin', label: 'Verlichting', rate: 0.11 },
  { id: 'woondecoratie', group: 'Huis & Tuin', label: 'Woondecoratie', rate: 0.13 },
  // Kleding, Schoenen & Sieraden
  { id: 'kleding', group: 'Kleding, Schoenen & Sieraden', label: 'Kleding', rate: 0.15 },
  { id: 'schoenen', group: 'Kleding, Schoenen & Sieraden', label: 'Schoenen', rate: 0.15 },
  { id: 'sieraden-horloges', group: 'Kleding, Schoenen & Sieraden', label: 'Sieraden & horloges', rate: 0.13 },
  { id: 'tassen-accessoires', group: 'Kleding, Schoenen & Sieraden', label: 'Tassen & accessoires', rate: 0.14 },
  // Beauty & Gezondheid
  { id: 'parfum-cosmetica', group: 'Beauty & Gezondheid', label: 'Parfum & cosmetica', rate: 0.12 },
  { id: 'huidverzorging', group: 'Beauty & Gezondheid', label: 'Huidverzorging', rate: 0.12 },
  { id: 'gezondheidsproducten', group: 'Beauty & Gezondheid', label: 'Gezondheidsproducten', rate: 0.10 },
  { id: 'vitamines-supplementen', group: 'Beauty & Gezondheid', label: 'Vitamines & supplementen', rate: 0.10 },
  // Sport & Vrije tijd
  { id: 'sportartikelen', group: 'Sport & Vrije tijd', label: 'Sportartikelen', rate: 0.11 },
  { id: 'fietsen', group: 'Sport & Vrije tijd', label: 'Fietsen & accessoires', rate: 0.09 },
  { id: 'outdoor-camping', group: 'Sport & Vrije tijd', label: 'Outdoor & camping', rate: 0.11 },
  // Baby & Kind
  { id: 'babyverzorging', group: 'Baby & Kind', label: 'Babyverzorging', rate: 0.09 },
  { id: 'speelgoed', group: 'Baby & Kind', label: 'Speelgoed', rate: 0.11 },
  { id: 'kinderkleding', group: 'Baby & Kind', label: 'Kinderkleding', rate: 0.14 },
  // Boeken, Media & Entertainment
  { id: 'boeken', group: 'Boeken, Media & Entertainment', label: 'Boeken', rate: 0.14 },
  { id: 'muziek-vinyl', group: 'Boeken, Media & Entertainment', label: 'Muziek & vinyl', rate: 0.12 },
  { id: 'games-software', group: 'Boeken, Media & Entertainment', label: 'Games & software', rate: 0.10 },
  { id: 'films-series', group: 'Boeken, Media & Entertainment', label: 'Films & series', rate: 0.12 },
  // Kantoor & Zakelijk
  { id: 'kantoorartikelen', group: 'Kantoor & Zakelijk', label: 'Kantoorartikelen', rate: 0.12 },
  { id: 'printers-inkt', group: 'Kantoor & Zakelijk', label: 'Printers & inkt', rate: 0.08 },
  { id: 'kantoormeubels', group: 'Kantoor & Zakelijk', label: 'Kantoormeubels', rate: 0.11 },
  // Dier
  { id: 'diervoeding', group: 'Dier', label: 'Diervoeding', rate: 0.09 },
  { id: 'dieraccessoires', group: 'Dier', label: 'Dieraccessoires', rate: 0.12 },
];

// ─── Amazon EU ──────────────────────────────────────────────────────

export interface AmazonReferralCategory {
  id: string;
  label: string;
  rate: number;
  minFee: number;
}

export interface AmazonFbaSizeTier {
  id: string;
  label: string;
  maxDimension: string;
  maxWeight: number;
  baseFee: number;
  perKgAbove: number;
  perKgRate: number;
}

export const AMAZON_DATA_UPDATED = '2026-03-01';
export const AMAZON_MONTHLY_FEE = 39.00;
export const AMAZON_VAT_RATE = 0.21;

export const AMAZON_REFERRAL_CATEGORIES: AmazonReferralCategory[] = [
  { id: 'electronics', label: 'Elektronica', rate: 0.07, minFee: 0.30 },
  { id: 'computers', label: 'Computers', rate: 0.07, minFee: 0.30 },
  { id: 'video-games', label: 'Games & software', rate: 0.15, minFee: 0.30 },
  { id: 'home-garden', label: 'Huis & tuin', rate: 0.15, minFee: 0.30 },
  { id: 'kitchen', label: 'Keuken', rate: 0.15, minFee: 0.30 },
  { id: 'furniture', label: 'Meubels', rate: 0.15, minFee: 0.30 },
  { id: 'clothing', label: 'Kleding & accessoires', rate: 0.15, minFee: 0.30 },
  { id: 'shoes', label: 'Schoenen & tassen', rate: 0.15, minFee: 0.30 },
  { id: 'beauty', label: 'Beauty & persoonlijke verzorging', rate: 0.08, minFee: 0.30 },
  { id: 'health', label: 'Gezondheid & huishouden', rate: 0.08, minFee: 0.30 },
  { id: 'sports', label: 'Sport & outdoor', rate: 0.15, minFee: 0.30 },
  { id: 'toys', label: 'Speelgoed & spellen', rate: 0.15, minFee: 0.30 },
  { id: 'baby', label: 'Baby', rate: 0.08, minFee: 0.30 },
  { id: 'pet-supplies', label: 'Huisdierbenodigdheden', rate: 0.15, minFee: 0.30 },
  { id: 'automotive', label: 'Auto & motor', rate: 0.15, minFee: 0.30 },
  { id: 'books', label: 'Boeken', rate: 0.15, minFee: 0.30 },
  { id: 'music', label: 'Muziek, CD & vinyl', rate: 0.15, minFee: 0.30 },
  { id: 'office', label: 'Kantoorartikelen', rate: 0.15, minFee: 0.30 },
  { id: 'grocery', label: 'Levensmiddelen', rate: 0.08, minFee: 0.30 },
  { id: 'handmade', label: 'Handgemaakt', rate: 0.12, minFee: 0.30 },
];

export const AMAZON_FBA_SIZE_TIERS: AmazonFbaSizeTier[] = [
  { id: 'small-envelope', label: 'Kleine envelop', maxDimension: '33 × 23 × 2,5 cm', maxWeight: 0.5, baseFee: 1.89, perKgAbove: 0, perKgRate: 0 },
  { id: 'standard-envelope', label: 'Standaard envelop', maxDimension: '33 × 23 × 2,5 cm', maxWeight: 1.0, baseFee: 2.18, perKgAbove: 0.5, perKgRate: 0.10 },
  { id: 'small-parcel', label: 'Klein pakket', maxDimension: '35 × 25 × 12 cm', maxWeight: 3.0, baseFee: 2.89, perKgAbove: 0.5, perKgRate: 0.10 },
  { id: 'standard-parcel', label: 'Standaard pakket', maxDimension: '45 × 34 × 26 cm', maxWeight: 12.0, baseFee: 3.49, perKgAbove: 1.0, perKgRate: 0.16 },
  { id: 'small-oversize', label: 'Klein oversize', maxDimension: '61 × 46 × 46 cm', maxWeight: 15.0, baseFee: 5.70, perKgAbove: 2.0, perKgRate: 0.20 },
  { id: 'standard-oversize', label: 'Standaard oversize', maxDimension: '120 × 60 × 60 cm', maxWeight: 30.0, baseFee: 7.90, perKgAbove: 2.0, perKgRate: 0.42 },
  { id: 'large-oversize', label: 'Groot oversize', maxDimension: '> 120 cm', maxWeight: 31.5, baseFee: 14.95, perKgAbove: 2.0, perKgRate: 0.65 },
];

// ─── Etsy ───────────────────────────────────────────────────────────

export const ETSY_DATA_UPDATED = '2026-03-01';
export const ETSY_LISTING_FEE = 0.18;
export const ETSY_TRANSACTION_FEE_RATE = 0.065;
export const ETSY_PAYMENT_PROCESSING_RATE = 0.04;
export const ETSY_PAYMENT_PROCESSING_FIXED = 0.30;
export const ETSY_OFFSITE_ADS_RATE_UNDER_10K = 0.15;
export const ETSY_OFFSITE_ADS_RATE_OVER_10K = 0.12;
export const ETSY_REGULATORY_FEE_RATE = 0.0035;

// ─── Shopify ────────────────────────────────────────────────────────

export interface ShopifyPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPricePerMonth: number;
  transactionFeeRate: number;
  creditCardRateOnline: number;
  creditCardFixedOnline: number;
}

export const SHOPIFY_DATA_UPDATED = '2026-03-01';

export const SHOPIFY_PLANS: ShopifyPlan[] = [
  { id: 'basic', name: 'Basic', monthlyPrice: 36, yearlyPricePerMonth: 27, transactionFeeRate: 0.02, creditCardRateOnline: 0.029, creditCardFixedOnline: 0.30 },
  { id: 'shopify', name: 'Shopify', monthlyPrice: 92, yearlyPricePerMonth: 69, transactionFeeRate: 0.01, creditCardRateOnline: 0.027, creditCardFixedOnline: 0.30 },
  { id: 'advanced', name: 'Advanced', monthlyPrice: 384, yearlyPricePerMonth: 289, transactionFeeRate: 0.005, creditCardRateOnline: 0.025, creditCardFixedOnline: 0.30 },
];
